import { motion } from "framer-motion";
import {
  ArrowLeft,
  Building2,
  Coffee,
  Landmark,
  UtensilsCrossed,
  Bike,
  Ship,
  Megaphone,
  Check,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Mail, Link as LinkIcon } from "lucide-react";
import { Reveal } from "@/components/motion";
import { AdPurchase } from "@/components/AdPurchase";
import { Seo } from "@/components/Seo";

const STATS = [
  { big: "25–45", small: "The age of our core readers" },
  { big: "1st & 2nd", small: "-time visitors, actively planning" },
  { big: "One page", small: "Everything they need — including you" },
  { big: "High intent", small: "Readers building a real itinerary now" },
];

const OPPORTUNITIES = [
  {
    icon: Building2,
    title: "Hotels & hostels",
    color: "#DF3226",
    text: "Be the bed they book. Land in our 'Where to sleep' shortlist right as they decide where to stay.",
  },
  {
    icon: Coffee,
    title: "Bakeries & cafés",
    color: "#FDE047",
    text: "A cardamom bun can make a trip. Get your morning queue featured next to the city's cult favourites.",
  },
  {
    icon: Landmark,
    title: "Museums & attractions",
    color: "#1E3A8A",
    text: "Turn a 'maybe' into a must-do. Slot your exhibition straight into their day-by-day plan.",
  },
  {
    icon: UtensilsCrossed,
    title: "Restaurants & bars",
    color: "#0A0A0A",
    text: "Hungry, deciding, right now. Own the lunch or dinner slot in the neighbourhoods they're already walking.",
  },
  {
    icon: Bike,
    title: "Bike & tour operators",
    color: "#DF3226",
    text: "They're renting a bike anyway. Be the ride, the tour or the harbour swim they add to the itinerary.",
  },
  {
    icon: Ship,
    title: "Day trips & experiences",
    color: "#1E3A8A",
    text: "Castles, beaches, Malmö. Reach travellers with a free day and an appetite for one more adventure.",
  },
];

const PLACEMENTS = [
  "Featured card inside the relevant neighbourhood & area pages",
  "A sponsored stop that drops straight into the interactive day planner",
  "Hero spot in the section your audience already reads (Sleep, Eat, Bikes…)",
];

export default function AdvertisePage() {
  return (
    <main data-testid="advertise-page" className="pt-16 md:pt-20">
      <Seo
        title="Advertise on CPH Highlights — Reach Copenhagen Visitors"
        description="Put your hotel, restaurant, tour or shop in front of engaged travellers actively planning a trip to Copenhagen."
        path="/advertise"
      />
      {/* hero */}
      <section className="bg-[#13141A] text-white border-b border-white/20">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24">
          <Link
            to="/"
            data-testid="advertise-back-home"
            className="inline-flex items-center gap-2 font-body font-700 text-sm bg-white text-black px-5 py-2.5 rounded-full border border-white/20 mb-8 hover:bg-[#DF3226] hover:text-white hover:border-[#DF3226] transition-colors"
          >
            <ArrowLeft size={16} /> Back to start
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226]">PARTNER WITH US</p>
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="font-display font-900 tracking-tighter leading-[0.85] text-4xl sm:text-5xl lg:text-6xl"
          >
            Get in front of<br />
            <span className="font-editorial italic font-600">everyone planning Copenhagen</span>
          </motion.h1>
          <p className="font-body text-base md:text-lg text-white/80 max-w-2xl mt-6">
            CPH Highlights is where first- and second-time visitors decide what to do, where to eat and
            where to sleep — all on one page, itinerary in hand. That&apos;s a rare thing: an audience that&apos;s
            not just browsing, but actually booking. Why not be the place they choose?
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href="#packages"
              data-testid="advertise-cta-hero"
              className="inline-flex items-center gap-2 font-display font-900 text-lg bg-[#DF3226] text-white px-8 py-4 rounded-full border border-white/20 shadow-brut hover:-translate-y-0.5 hover:shadow-xl transition-all"
            >
              <Megaphone size={18} /> Send an enquiry
            </a>
          </div>
        </div>
      </section>

      {/* stats */}
      <section className="bg-[#DF3226] text-white border-b border-zinc-200">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-16 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {STATS.map((s, i) => (
            <Reveal key={s.big} delay={i * 0.05}>
              <div data-testid={`advertise-stat-${i}`}>
                <p className="font-display font-900 tracking-tighter leading-none text-3xl md:text-4xl">{s.big}</p>
                <p className="font-body font-700 text-sm text-white/85 mt-2">{s.small}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* opportunities */}
      <section className="bg-[#F9F8F6]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24">
          <Reveal>
            <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl sm:text-5xl lg:text-6xl max-w-3xl">
              Whatever you run,<br />
              <span className="font-editorial italic font-600">there&apos;s a spot for you</span>
            </h2>
            <p className="font-body text-base md:text-lg text-black/70 max-w-2xl mt-6">
              Hotel, bakery, museum, restaurant, bike shop or day trip — if it makes a Copenhagen visit
              better, our readers want to know about it. Here&apos;s where you&apos;d fit.
            </p>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {OPPORTUNITIES.map((o, i) => {
              const Icon = o.icon;
              return (
                <Reveal key={o.title} delay={i * 0.05}>
                  <div
                    data-testid={`advertise-opportunity-${i}`}
                    className="bg-white border border-zinc-200 shadow-brut hover:-translate-y-1 hover:shadow-brut-lg transition-all p-6 h-full flex flex-col"
                  >
                    <span
                      className="w-12 h-12 grid place-items-center border border-zinc-200 mb-5"
                      style={{ backgroundColor: o.color, color: o.color === "#FDE047" ? "#0A0A0A" : "#FFFFFF" }}
                    >
                      <Icon size={22} />
                    </span>
                    <h3 className="font-display font-900 tracking-tight text-xl md:text-2xl">{o.title}</h3>
                    <p className="font-body text-sm text-black/70 leading-relaxed mt-3">{o.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* placements */}
      <section className="bg-white border-y border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226] mb-4">HOW IT WORKS</p>
              <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl sm:text-5xl">
                Not a banner they&apos;ll ignore<br />
                <span className="font-editorial italic font-600">Part of the plan</span>
              </h2>
              <p className="font-body text-base md:text-lg text-black/70 mt-6 max-w-md">
                We keep it tasteful and useful — placements that read like a genuine recommendation, so
                travellers actually act on them.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <div className="bg-[#F9F8F6] border border-zinc-200 shadow-brut p-6 md:p-8">
                <ul className="space-y-4">
                  {PLACEMENTS.map((p) => (
                    <li key={p} className="flex gap-3 items-start">
                      <Check size={20} className="text-[#DF3226] shrink-0 mt-0.5" />
                      <span className="font-body text-base leading-relaxed">{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* pricing + purchase */}
      <AdPurchase />

      {/* already-listed link request */}
      <section data-testid="link-request" className="bg-[#F9F8F6]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 pb-20 md:pb-28">
          <div className="bg-[#13141A] text-white rounded-2xl p-8 md:p-12 flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
            <span className="w-14 h-14 grid place-items-center bg-[#FDE047] text-[#DF3226] rounded-2xl shrink-0">
              <LinkIcon size={26} />
            </span>
            <div className="flex-1">
              <h2 className="font-display font-900 tracking-tight text-2xl md:text-3xl">
                Already mentioned, but missing a link?
              </h2>
              <p className="font-body text-base text-white/75 mt-2 max-w-2xl leading-relaxed">
                If your place already appears in our guide and you&apos;d simply like it to link out to
                your website, just reach out — we&apos;ll get it sorted for you.
              </p>
            </div>
            <a
              href="mailto:cphhighlights@gmail.com?subject=Add%20a%20link%20to%20our%20listing"
              data-testid="link-request-cta"
              className="inline-flex items-center gap-2 font-display font-900 text-lg bg-white text-[#DF3226] px-7 py-4 rounded-full shrink-0 hover:-translate-y-0.5 transition-all"
            >
              <Mail size={18} /> Get in touch
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
